import { useState, useEffect, useRef } from 'react';
import { Building2, Plus, Pencil, Trash2, X, Save, Search, ChevronDown, Calendar, Loader2, MailPlus, ImagePlus } from 'lucide-react';
import { WzSearch } from './WzSearch';
import { buildCustomerUpdatePayload, buildCustomerCreatePayload } from './customerPayload';
import { DataRequestModal } from './DataRequestModal';
import { api } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import { lookupPlz } from '../lib/plzLookup';
import ConfirmDialog from './admin/ConfirmDialog';
import { BUNDESLAENDER, getElsterBundesland, computeElster } from '../lib/elster';
import { BafaCheckBadge, type BafaReason } from './BafaCheckBadge';
import { CustomerLogo } from './CustomerLogo';
import { logoAlsDataUrl, pruefeLogoDatei, LOGO_TYPEN } from '../lib/logoBild';
import { fehlendePflichtfelder } from '../lib/pflichtfelder';
import { FoerderListe, type FoerderEintrag } from './FoerderListe';
import { BafaCheckPopup } from './BafaCheckPopup';
import { SteuerIdWarnModal } from './SteuerIdWarnModal';
import { istVermutlichSteuernummerStattIdnr } from '../lib/steuerId';
import { isValidIsoDate } from './DateTextInput';

function parseDateInput(input: string): string {
  const trimmed = input.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return trimmed;
}

function formatDateDisplay(isoDate: string): string {
  if (!isoDate) return '';
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return isoDate;
}

function DateTextInputCustomer({ value, onChange, darkMode, className, hasError }: {
  value: string;
  onChange: (val: string) => void;
  darkMode: boolean;
  className?: string;
  hasError?: boolean;
}) {
  const dateRef = useRef<HTMLInputElement>(null);
  // Während des Tippens NICHT bei jedem Tastendruck neu formatieren/padden — das ließ den
  // Cursor springen und einzelne Ziffern beim Ändern einer Stelle "verschwinden"/auf 0
  // springen (Kundenfeedback 16.10.2026, siehe auch DateTextInput.tsx). Rohtext bleibt
  // während des Fokus frei editierbar, erst beim Verlassen des Felds wird
  // geparst/formatiert/committed — und nur, wenn das Datum real existiert (nicht nur das
  // Format stimmt, z.B. "14.15.2027" wurde bisher klaglos übernommen).
  const [draft, setDraft] = useState<string | null>(null);
  const displayValue = draft !== null ? draft : formatDateDisplay(value);

  return (
    <div className="relative">
      <input
        translate="no"
        type="text"
        value={displayValue}
        onFocus={() => setDraft(formatDateDisplay(value))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          if (raw) {
            const parsed = parseDateInput(raw);
            if (isValidIsoDate(parsed)) onChange(parsed);
          } else {
            onChange('');
          }
          setDraft(null);
        }}
        placeholder="tt.mm.jjjj"
        className={className || `w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
          darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'
        }`}
        style={{ paddingRight: '2.5rem' }}
      />
      <input
        translate="no"
        ref={dateRef}
        type="date"
        value={/^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''}
        onChange={(e) => { onChange(e.target.value); setDraft(null); }}
        className="absolute inset-0 opacity-0 pointer-events-none"
        tabIndex={-1}
      />
      <button
        type="button"
        onClick={() => dateRef.current?.showPicker?.()}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
        }`}
      >
        <Calendar className="w-4 h-4" />
      </button>
    </div>
  );
}

export interface Customer {
  id: number;
  name: string;
  email: string;
  verteilerEmail?: string;
  phone: string;
  address: string;
  plz: string;
  ort: string;
  personentyp?: string;
  rechtsform?: string;
  wirtschaftszweig?: string;
  steueridentnummer?: string;
  steuernummer?: string;
  bundesland?: string;
  geburtsdatum?: string;
  gruendung?: string;
  mitarbeiterzahl?: string;
  website?: string;
  /** Selbst hochgeladenes Logo als Data-URL; leer → Favicon der Website. */
  logoUrl?: string;
  anrede?: string;
  vorname: string;
  nachname: string;
  contactPerson?: string;
  jahresbilanzsumme?: string;
  jahresumsatz?: string;
  geschaeftsgegenstand?: string;
  taxId?: string;
  notes?: string;
  createdAt?: string;
  land?: string;
  artJuristischePerson?: string;
  vorsteuerabzug?: string;
  unternehmenstyp?: string;
  oeffentlicheBeteiligung?: boolean;
  antragstellerTyp?: string;
  hausnummer?: string;
  phoneVorwahl?: string;
  phoneNummer?: string;
  iban?: string;
  unternehmenInSchwierigkeiten?: boolean;
  vorfoerderungUeberDeMinimis?: boolean;
  foerderfaehigkeitAmpel?: string | null;
  foerderfaehigkeitKmuKlasse?: string | null;
  foerderfaehigkeitReasons?: BafaReason[] | null;
  foerderfaehigkeitGeprueftAt?: string | null;
  foerderungen?: FoerderEintrag[];
  /** Kunde abgesprungen/inaktiv — aus Dashboard/Aufgaben ausgeblendet, Daten bleiben. */
  archived?: boolean;
}

interface CustomerManagementProps {
  darkMode: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onAddNew: () => void;
  onRequestData?: () => void;
  hideManageButton?: boolean;
  /** Ohne Kartenfläche und ohne Innenabstand — für Seiten, die den Kopf nur als
   *  Titelzeile brauchen und den Platz für die Liste selbst wollen. */
  flach?: boolean;
  /** Werkzeuge (z.B. Suche, Spaltenwahl), die links neben den Knöpfen im Kopf stehen. */
  werkzeuge?: React.ReactNode;
  /** Zusätzliche Klassen für die Root-Div (werden angehängt, ersetzen nichts). */
  className?: string;
  children?: React.ReactNode;
}

export function CustomerManagement({ darkMode, isExpanded, onToggleExpanded, onAddNew, onRequestData, hideManageButton, flach, werkzeuge, className, children }: CustomerManagementProps) {
  return (
    <div className={`${flach ? '' : `rounded-xl border shadow-sm ${
      darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
    }`}${className ? ` ${className}` : ''}`}>
      {/* Compact Header */}
      <div className={flach ? '' : 'p-4 sm:p-6'}>
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0 basis-full sm:basis-0">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className={`text-xl font-semibold mb-0.5 ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}>
                Kundenverwaltung
              </h2>
              <p className={`text-sm ${
                darkMode ? 'text-slate-400' : 'text-slate-600'
              }`}>
                Alle Kundendaten an einem Ort — Grundlage jedes Berichts.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-xl sm:justify-end">
          {werkzeuge && (
            <div className="flex-1 min-w-0">
              {werkzeuge}
            </div>
          )}
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onAddNew}
              title="Neuer Kunde"
              aria-label="Neuer Kunde"
              className="w-12 h-12 aspect-square flex items-center justify-center bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm hover:shadow-md cursor-pointer"
            >
              <Plus className="w-5 h-5" />
            </button>
            {onRequestData && (
              <button
                onClick={onRequestData}
                title="Daten anfordern"
                aria-label="Daten anfordern"
                className="w-12 h-12 aspect-square flex items-center justify-center bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors shadow-sm hover:shadow-md cursor-pointer"
              >
                <MailPlus className="w-5 h-5" />
              </button>
            )}
            {!hideManageButton && (
              <button
                onClick={onToggleExpanded}
                title={isExpanded ? 'Schließen' : 'Verwalten'}
                aria-label={isExpanded ? 'Schließen' : 'Verwalten'}
                className="w-12 h-12 aspect-square flex items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-sm hover:shadow-md cursor-pointer"
              >
                {isExpanded ? <X className="w-5 h-5" /> : <Pencil className="w-5 h-5" />}
              </button>
            )}
          </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

// Separate Customer List Card Component
interface CustomerListCardProps {
  darkMode: boolean;
  isExpanded: boolean;
  triggerNewCustomer?: number;
  /** `fokusLuecke` öffnet den Editor und rollt zum ersten leeren Pflichtfeld. */
  triggerEditCustomer?: {id: number, ts: number, fokusLuecke?: boolean} | null;
  onChanged?: () => void;
}

export function CustomerListCard({ darkMode, isExpanded, triggerNewCustomer, triggerEditCustomer, onChanged }: CustomerListCardProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dataRequestModalEmail, setDataRequestModalEmail] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [logoFehler, setLogoFehler] = useState<string | null>(null);
  /** Beim Öffnen aus dem Hinweis „n fehlen": zum ersten leeren Pflichtfeld rollen. */
  const [scrollZurLuecke, setScrollZurLuecke] = useState(0);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [entriesDropdownOpen, setEntriesDropdownOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Customer>>({
    name: '',
    email: '',
    verteilerEmail: '',
    phone: '',
    address: '',
    plz: '',
    ort: '',
    personentyp: '',
    rechtsform: '',
    wirtschaftszweig: '',
    steueridentnummer: '',
    steuernummer: '',
    geburtsdatum: '',
    gruendung: '',
    mitarbeiterzahl: '',
    website: '',
    anrede: '',
    vorname: '',
    nachname: '',
    contactPerson: '',
    jahresbilanzsumme: '',
    jahresumsatz: '',
    geschaeftsgegenstand: '',
    taxId: '',
    notes: '',
    land: '',
    artJuristischePerson: '',
    vorsteuerabzug: '',
    unternehmenstyp: '',
    oeffentlicheBeteiligung: true,
    unternehmenInSchwierigkeiten: false,
    vorfoerderungUeberDeMinimis: false,
  });
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string>('');
  const [checkPopup, setCheckPopup] = useState<{ open: boolean; ampel?: string | null; reasons?: { code: string; severity: string }[] }>({ open: false });
  const [steuerIdWarnOpen, setSteuerIdWarnOpen] = useState(false);
  // Nicht bei jedem Blur erneut nerven, solange sich der (falsche) Wert nicht ändert.
  const steuerIdWarnedValueRef = useRef<string | null>(null);

  // Load customers from API
  const loadCustomers = () => {
    api.get<Customer[]>('/api/customers').then(setCustomers).catch(() => {});
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  // Trigger new customer from parent
  useEffect(() => {
    if (triggerNewCustomer && triggerNewCustomer > 0) {
      handleAddNew();
    }
  }, [triggerNewCustomer]);

  // Trigger edit customer from parent — always fetch to ensure fresh data
  // Using {id, ts} object so clicking same customer twice always triggers
  useEffect(() => {
    if (triggerEditCustomer != null) {
      const danach = () => { if (triggerEditCustomer.fokusLuecke) setScrollZurLuecke(triggerEditCustomer.ts); };
      api.get<Customer>(`/api/customers/${triggerEditCustomer.id}`)
        .then(c => { handleEdit(c); danach(); })
        .catch(() => {
          const customer = customers.find(c => Number(c.id) === Number(triggerEditCustomer.id));
          if (customer) { handleEdit(customer); danach(); }
        });
    }
  }, [triggerEditCustomer]);


  const handleAddNew = () => {
    setEditingCustomer(null);
    setValidationErrors(new Set());
    setSaveError('');
    setFormData({
      name: '',
      email: '',
      verteilerEmail: '',
      phone: '',
      address: '',
      plz: '',
      ort: '',
      rechtsform: '',
      wirtschaftszweig: '',
      gruendung: '',
      mitarbeiterzahl: '',
      website: '',
      anrede: '',
      vorname: '',
      nachname: '',
      contactPerson: '',
      jahresbilanzsumme: '',
      jahresumsatz: '',
      geschaeftsgegenstand: '',
      taxId: '',
      notes: '',
      land: '',
      artJuristischePerson: '',
      vorsteuerabzug: '',
      unternehmenstyp: '',
      oeffentlicheBeteiligung: true,
      unternehmenInSchwierigkeiten: false,
      vorfoerderungUeberDeMinimis: false,
      geburtsdatum: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData(customer);
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteConfirmId(id);
  };

  const performDelete = async () => {
    if (deleteConfirmId === null) return;
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    try {
      await api.delete(`/api/customers/${id}`);
      loadCustomers();
      onChanged?.();
    } catch {
      // ignore
    }
  };

  // Für „Daten anfordern": stellt sicher, dass der Kunde existiert und die E-Mail
  // an ihm gespeichert ist, und liefert die customerId für die Einladung.
  const resolveCustomerForInvite = async (email: string): Promise<number | undefined> => {
    if (editingCustomer) {
      // Bestandskunde: E-Mail am Kunden speichern.
      await api.patch(`/api/customers/${editingCustomer.id}`, { email });
      setFormData(prev => ({ ...prev, email }));
      await loadCustomers();
      onChanged?.();
      return editingCustomer.id;
    }
    // Neuanlage: Kunden mit den bisherigen Feldern + E-Mail anlegen.
    const { id, contactPerson, ...raw } = { ...formData, email } as Customer;
    const payload = buildCustomerCreatePayload(raw as Record<string, unknown>);
    const created = await api.post<Customer>('/api/customers', payload);
    setEditingCustomer(created);
    setFormData(prev => ({ ...prev, email }));
    await loadCustomers();
    onChanged?.();
    return created.id;
  };

  const handleSave = async () => {
    if (isSaving) return;
    setValidationErrors(new Set());
    setSaveError('');

    // Hinweis: Firmenname ist Pflicht (DB: customers.name NOT NULL). Das Speichern
    // wird dadurch NICHT blockiert — ist der Name leer, bleibt der bisherige Wert
    // erhalten (buildCustomerUpdatePayload lässt ihn weg; Backend überschreibt nicht).
    // Der Hinweis am Feld informiert den Nutzer, verhindert das Speichern aber nicht.

    // Duplikat-Prüfung: gleicher Name + Rechtsform + Ort
    if (!editingCustomer) {
      const nameLower = (formData.name || '').trim().toLowerCase();
      const rechtsformLower = (formData.rechtsform || '').trim().toLowerCase();
      const ortLower = (formData.ort || '').trim().toLowerCase();
      const duplicate = customers.find(c =>
        c.name.trim().toLowerCase() === nameLower &&
        (c.rechtsform || '').trim().toLowerCase() === rechtsformLower &&
        (c.ort || '').trim().toLowerCase() === ortLower
      );
      if (duplicate) {
        setSaveError(`Ein Kunde mit dem Namen "${duplicate.name}" (${duplicate.rechtsform}${duplicate.ort ? ', ' + duplicate.ort : ''}) existiert bereits.`);
        return;
      }
    }

    setIsSaving(true);

    try {
      const { id, contactPerson, ...raw } = formData as Customer;

      if (editingCustomer) {
        // Bei Updates: leere Strings als null senden, damit Felder wirklich geleert werden.
        // Pflichtfelder (name) werden dabei NIE genullt (siehe buildCustomerUpdatePayload).
        const payload = buildCustomerUpdatePayload(raw as Record<string, unknown>);
        const updated = await api.patch(`/api/customers/${editingCustomer.id}`, payload);
        // Badge im Modal sofort auf das frisch berechnete Ergebnis aktualisieren.
        if (updated) setEditingCustomer(updated as Customer);
      } else {
        // Bei Neuanlage: leere Felder herausfiltern (siehe buildCustomerCreatePayload).
        const payload = buildCustomerCreatePayload(raw as Record<string, unknown>);
        const created = await api.post<Customer>('/api/customers', payload);
        if (created) {
          setCheckPopup({ open: true, ampel: created.foerderfaehigkeitAmpel, reasons: created.foerderfaehigkeitReasons ?? [] });
          if (created.foerderungen) {
            setFormData(prev => ({ ...prev, foerderungen: created.foerderungen }));
          }
        }
      }
      await loadCustomers();
      onChanged?.();
      setIsModalOpen(false);
      setFormData({
        name: '', email: '', verteilerEmail: '', phone: '', address: '', plz: '', ort: '',
        rechtsform: '', wirtschaftszweig: '', gruendung: '', mitarbeiterzahl: '',
        website: '', anrede: '', vorname: '', nachname: '', contactPerson: '',
        jahresbilanzsumme: '', jahresumsatz: '', geschaeftsgegenstand: '', taxId: '', notes: '',
        land: '', artJuristischePerson: '', vorsteuerabzug: '', unternehmenstyp: '', oeffentlicheBeteiligung: true,
        unternehmenInSchwierigkeiten: false, vorfoerderungUeberDeMinimis: false,
        geburtsdatum: '',
      });
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setSaveError(err?.message || 'Fehler beim Speichern. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSaving(false);
    }
  };

  // Validation helpers
  const hasVal = (v: any) => v !== undefined && v !== null && String(v).trim() !== '';
  // Felder, die in die Vollständigkeits-% einfließen (Spiegel von Customers.tsx).
  const COMPLETION_BASE = ['name', 'rechtsform', 'antragstellerTyp', 'gruendung', 'anrede', 'vorname', 'nachname', 'address', 'hausnummer', 'plz', 'ort', 'land', 'email', 'phoneVorwahl', 'phoneNummer', 'iban', 'website', 'wirtschaftszweig', 'personentyp', 'vorsteuerabzug', 'mitarbeiterzahl', 'unternehmenstyp', 'geschaeftsgegenstand', 'taxId'];
  // Ist ein vollständigkeits-relevantes Feld noch leer? → orange markieren.
  const isMissing = (field: string): boolean => {
    if (hasVal(formData[field as keyof typeof formData])) return false;
    if (field === 'jahresumsatz' || field === 'jahresbilanzsumme')
      return !hasVal(formData.jahresumsatz) && !hasVal(formData.jahresbilanzsumme);
    if (COMPLETION_BASE.includes(field)) return true;
    if (formData.personentyp === 'Natürliche Person') return field === 'steueridentnummer' || field === 'geburtsdatum';
    if (formData.personentyp === 'Juristische Person') return field === 'artJuristischePerson' || field === 'steuernummer';
    return false;
  };

  const inputClass = (fieldKey: string) => {
    const base = `feld-${fieldKey} w-full px-3 sm:px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`;
    if (isMissing(fieldKey)) {
      // Klar erkennbares Orange für fehlende Vollständigkeitsfelder.
      return `${base} border-orange-400 ${darkMode ? 'bg-orange-400/10 text-white' : 'bg-orange-50 text-slate-900'}`;
    }
    const borderColor = darkMode ? 'border-slate-600' : 'border-slate-300';
    const colors = darkMode ? 'bg-slate-700 text-white' : 'bg-white text-slate-900';
    return `${base} ${borderColor} ${colors}`;
  };

  // Label-Klasse für Felder ohne eigenes Input zum Einfärben (Checkbox-Gruppen) — orange wenn fehlt.
  // Überschriften werden NICHT mehr eingefärbt — fehlende Pflichtangaben werden
  // einheitlich nur am Feld markiert (siehe inputClass). reqLabel bleibt als
  // einheitliche Label-Klasse erhalten.
  /** Beschriftung eines einzelnen Feldes — bewusst kleiner als die Abschnittstitel. */
  const feldLabel = `block text-xs font-semibold mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;
  /** Wie feldLabel, zusätzlich als Sprungziel für „n fehlen" (Gruppen ohne eigenes Eingabefeld). */
  const reqLabel = (field: string) =>
    `feld-${field} block text-xs font-semibold mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;

  const fieldError = (fieldKey: string) => validationErrors.has(fieldKey);

  // Aus dem Hinweis „n fehlen" geöffnet: zum ersten leeren Pflichtfeld rollen und
  // es fokussieren. Der Timeout wartet auf den ersten Rendervorgang des Dialogs.
  useEffect(() => {
    if (!scrollZurLuecke || !isModalOpen) return;
    const erste = fehlendePflichtfelder(formData as Record<string, unknown>)[0];
    if (!erste) return;
    const timer = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`.feld-${erste}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
        el.focus({ preventScroll: true });
      }
    }, 120);
    return () => window.clearTimeout(timer);
    // formData absichtlich nicht in den Abhängigkeiten: gerollt wird einmal pro Öffnen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollZurLuecke, isModalOpen]);

  /** Bild aus dem Dateidialog: prüfen, im Browser verkleinern, als Data-URL ins Formular. */
  const handleLogoDatei = async (file: File | undefined) => {
    if (!file) return;
    const fehler = pruefeLogoDatei(file);
    if (fehler) { setLogoFehler(fehler); return; }
    setLogoFehler(null);
    try {
      const dataUrl = await logoAlsDataUrl(file);
      setFormData(prev => ({ ...prev, logoUrl: dataUrl }));
    } catch {
      setLogoFehler('Bild konnte nicht gelesen werden.');
    }
  };

  const clearError = (fieldKey: string) => {
    if (validationErrors.has(fieldKey)) {
      const next = new Set(validationErrors);
      next.delete(fieldKey);
      setValidationErrors(next);
    }
  };

  // PLZ → Ort/Land: bei 5-stelliger PLZ die Orte laden (als Vorschläge fürs
  // Ort-Feld) und Land mit „Deutschland" vorbelegen (editierbar bleibt es).
  const [ortSuggestions, setOrtSuggestions] = useState<string[]>([]);
  const handlePlzChange = (value: string) => {
    setFormData((prev) => ({ ...prev, plz: value }));
    clearError('plz');
    if (!/^\d{5}$/.test(value)) { setOrtSuggestions([]); return; }
    lookupPlz(value).then((r) => {
      if (!r) { setOrtSuggestions([]); return; }
      setOrtSuggestions(r.orte);
      setFormData((prev) => ({
        ...prev,
        land: prev.land?.trim() ? prev.land : r.land,
        ort: r.orte.length === 1 ? r.orte[0] : prev.ort,
      }));
    });
  };

  // Filter customers based on search query
  const filteredCustomers = customers.filter((customer) => {
    const query = searchQuery.toLowerCase();
    return (
      (customer.name || '').toLowerCase().includes(query) ||
      (customer.email || '').toLowerCase().includes(query) ||
      (customer.contactPerson && customer.contactPerson.toLowerCase().includes(query)) ||
      (customer.phone && customer.phone.toLowerCase().includes(query)) ||
      (customer.ort && customer.ort.toLowerCase().includes(query))
    );
  });

  // Limit displayed customers based on entriesPerPage
  const displayedCustomers = filteredCustomers.slice(0, entriesPerPage);

  return (
    <>
      {/* Full Width Customer List Card */}
      <div className={`rounded-xl border shadow-sm overflow-hidden ${
        darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
      }`}>
        <div className="p-4 sm:p-6">
          {/* Search and Filter Controls */}
          <div className="mb-4 flex flex-col sm:flex-row gap-4">
            {/* Search Field */}
            <div className="w-full sm:max-w-md">
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}>
                Suche
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  translate="no"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Kundenname, E-Mail oder Ansprechperson..."
                  className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-slate-400 ${
                    darkMode
                      ? 'bg-slate-700 border-slate-600 text-white dark:placeholder:text-slate-500'
                      : 'bg-white border-slate-300 text-slate-900'
                  }`}
                />
              </div>
            </div>

            {/* Spacer to push Einträge to the right */}
            <div className="hidden sm:block sm:flex-1"></div>

            {/* Entries Per Page Dropdown */}
            <div className="relative w-full sm:w-16">
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}>
                Einträge
              </label>
              <button
                onClick={() => setEntriesDropdownOpen(!entriesDropdownOpen)}
                className={`w-full px-1.5 sm:px-2 py-2 border rounded-lg text-sm font-medium flex items-center justify-between gap-0.5 transition-colors cursor-pointer ${
                  darkMode 
                    ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' 
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span>{entriesPerPage}</span>
                <ChevronDown className="w-4 h-4 flex-shrink-0" />
              </button>
              
              {entriesDropdownOpen && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-10"
                    onClick={() => setEntriesDropdownOpen(false)}
                  />
                  
                  <div className={`absolute left-0 right-0 sm:left-auto sm:right-0 mt-2 rounded-lg shadow-lg z-20 border ${
                    darkMode 
                      ? 'bg-slate-700 border-slate-600' 
                      : 'bg-white border-slate-200'
                  }`}>
                    <div className="p-1">
                      <button
                        onClick={() => {
                          setEntriesPerPage(5);
                          setEntriesDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-sm rounded text-center transition-colors cursor-pointer ${
                          darkMode 
                            ? 'text-slate-300 hover:bg-slate-600' 
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        5
                      </button>
                      <button
                        onClick={() => {
                          setEntriesPerPage(10);
                          setEntriesDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-sm rounded text-center transition-colors cursor-pointer ${
                          darkMode 
                            ? 'text-slate-300 hover:bg-slate-600' 
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        10
                      </button>
                      <button
                        onClick={() => {
                          setEntriesPerPage(25);
                          setEntriesDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-sm rounded text-center transition-colors cursor-pointer ${
                          darkMode 
                            ? 'text-slate-300 hover:bg-slate-600' 
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        25
                      </button>
                      <button
                        onClick={() => {
                          setEntriesPerPage(50);
                          setEntriesDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-sm rounded text-center transition-colors cursor-pointer ${
                          darkMode 
                            ? 'text-slate-300 hover:bg-slate-600' 
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        50
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Customer List */}
          {displayedCustomers.length > 0 ? (
            <div className="space-y-3">
              {displayedCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className={`p-4 rounded-lg border flex items-start justify-between ${
                    darkMode 
                      ? 'bg-slate-800 border-slate-700 hover:bg-slate-750' 
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  } transition-colors`}
                >
                  <div className="flex-1">
                    <h3 translate="no" className={`font-medium mb-1 ${
                      darkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                      {customer.name}
                    </h3>
                    <div className={`text-sm space-y-1 ${
                      darkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      <p translate="no">{customer.email}</p>
                      {customer.phone && <p>Tel: <span translate="no">{customer.phone}</span></p>}
                      {customer.contactPerson && <p>Ansprechperson: <span translate="no">{customer.contactPerson}</span></p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(customer)}
                      className={`p-2 rounded-lg transition-colors cursor-pointer ${
                        darkMode 
                          ? 'hover:bg-slate-600 text-slate-400 hover:text-blue-400' 
                          : 'hover:bg-slate-100 text-slate-600 hover:text-blue-600'
                      }`}
                      title="Bearbeiten"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(customer.id)}
                      className={`p-2 rounded-lg transition-colors cursor-pointer ${
                        darkMode 
                          ? 'hover:bg-slate-600 text-slate-400 hover:text-red-400' 
                          : 'hover:bg-slate-100 text-slate-600 hover:text-red-600'
                      }`}
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-8 ${
              darkMode ? 'text-slate-400' : 'text-slate-500'
            }`}>
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">
                {searchQuery ? 'Keine Kunden gefunden' : 'Noch keine Kunden angelegt'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-2xl my-8 rounded-xl shadow-xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden ${
            darkMode ? 'bg-slate-800' : 'bg-white'
          }`}>
            <div className={`shrink-0 p-4 sm:p-6 border-b flex items-center justify-between ${
              darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            }`}>
              <h3 className={`text-lg sm:text-xl font-semibold ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}>
                {editingCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className={`p-2 rounded-lg transition-colors flex-shrink-0 cursor-pointer ${
                  darkMode 
                    ? 'hover:bg-slate-700 text-slate-400' 
                    : 'hover:bg-slate-100 text-slate-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6">
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Mit * gekennzeichnete Felder sind Pflichtfelder.
              </p>

              {/* Bild des Kunden: ohne eigenes Logo zeigt die Liste das Favicon der Website. */}
              <div className={`rounded-lg p-4 flex items-center gap-4 ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}>
                <CustomerLogo website={formData.website} logoUrl={formData.logoUrl} size="w-14 h-14" />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Bild des Kunden</p>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {formData.logoUrl
                      ? 'Eigenes Logo — wird in der Kundenliste angezeigt.'
                      : 'Zurzeit das Favicon der Website. Eigenes Bild hochladen, um es zu ersetzen.'}
                  </p>
                  {logoFehler && <p className="text-xs mt-1 text-red-500">{logoFehler}</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept={LOGO_TYPEN}
                      className="hidden"
                      onChange={(e) => { handleLogoDatei(e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        darkMode ? 'bg-slate-600 hover:bg-slate-500 text-white' : 'bg-white border border-slate-300 hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <ImagePlus className="w-3.5 h-3.5" />
                      {formData.logoUrl ? 'Bild ersetzen' : 'Bild hochladen'}
                    </button>
                    {formData.logoUrl && (
                      <button
                        type="button"
                        onClick={() => { setFormData(prev => ({ ...prev, logoUrl: '' })); setLogoFehler(null); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Entfernen
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Angaben zur antragstellenden Person */}
              <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}>
                <h4 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Angaben zur antragstellenden Person
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className={reqLabel('antragstellerTyp')}>
                      Bei der antragstellenden Person handelt es sich um
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {['Freiberuflich tätige Person', 'Unternehmen'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="antragstellerTyp" checked={formData.antragstellerTyp === opt} onChange={() => setFormData({ ...formData, antragstellerTyp: opt })} className="w-4 h-4 cursor-pointer" />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-4 items-end">
                    <div className="flex-[7]">
                      <label className={feldLabel}>Firmenname *</label>
                      <input translate="no" type="text" value={formData.name || ''} onChange={(e) => { setFormData({ ...formData, name: e.target.value }); clearError('name'); }} data-field-error={fieldError('name')} className={`${inputClass('name')} h-[38px]`} placeholder="" />
                      {editingCustomer && !(formData.name || '').trim() && (
                        <p className="mt-1 text-xs text-amber-600">Firmenname ist ein Pflichtfeld – der bisherige Name bleibt beim Speichern erhalten.</p>
                      )}
                    </div>
                    <div className="flex-[3]">
                      <label className={feldLabel}>Unternehmensform *</label>
                      <select translate="no" value={formData.rechtsform || ''} onChange={(e) => { setFormData({ ...formData, rechtsform: e.target.value }); clearError('rechtsform'); }} data-field-error={fieldError('rechtsform')} className={`${inputClass('rechtsform')} h-[38px]`}>
                        <option value="">Bitte wählen</option>
                        <optgroup label="Natürliche Person">
                          <option value="Einzelunternehmen">Einzelunternehmen</option>
                          <option value="Freiberufler/in">Freiberufler/in</option>
                          <option value="GbR">GbR</option>
                        </optgroup>
                        <optgroup label="Juristische Person (Kapitalgesellschaft)">
                          <option value="GmbH">GmbH</option>
                          <option value="mbH">mbH</option>
                          <option value="UG (haftungsbeschränkt)">UG (haftungsbeschränkt)</option>
                          <option value="AG">AG</option>
                          <option value="GmbH & Co. KG">GmbH & Co. KG</option>
                        </optgroup>
                        <optgroup label="Juristische Person (Personengesellschaft)">
                          <option value="OHG">OHG</option>
                          <option value="KG">KG</option>
                          <option value="Partnerschaftsgesellschaft">Partnerschaftsgesellschaft</option>
                        </optgroup>
                        <optgroup label="Sonstige">
                          <option value="e.V.">e.V.</option>
                          <option value="eG">eG</option>
                          <option value="KdöR">KdöR</option>
                          <option value="Stiftung">Stiftung</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ansprechperson */}
              <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}>
                <h4 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Ansprechperson
                </h4>
                <div className="flex gap-4">
                  <div className="flex-[2]">
                    <label className={feldLabel}>Anrede</label>
                    <select translate="no" value={formData.anrede || ''} onChange={(e) => { setFormData({ ...formData, anrede: e.target.value }); clearError('anrede'); }} data-field-error={fieldError('anrede')} className={`${inputClass('anrede')} h-[38px]`}>
                      <option value="">Bitte wählen</option>
                      <option value="Herr">Herr</option>
                      <option value="Frau">Frau</option>
                      <option value="Divers">Divers</option>
                    </select>
                  </div>
                  <div className="flex-[4]">
                    <label className={feldLabel}>Vorname</label>
                    <input translate="no" type="text" value={formData.vorname || ''} onChange={(e) => { setFormData({ ...formData, vorname: e.target.value }); clearError('vorname'); }} data-field-error={fieldError('vorname')} className={inputClass('vorname')} placeholder="" />
                  </div>
                  <div className="flex-[4]">
                    <label className={feldLabel}>Nachname</label>
                    <input translate="no" type="text" value={formData.nachname || ''} onChange={(e) => { setFormData({ ...formData, nachname: e.target.value }); clearError('nachname'); }} data-field-error={fieldError('nachname')} className={inputClass('nachname')} placeholder="" />
                  </div>
                </div>
              </div>

              {/* Adresse */}
              {/* Anschrift */}
              <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}>
                <h4 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Anschrift
                </h4>
                <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-[4]">
                    <label className={feldLabel}>Straße</label>
                    <input translate="no" type="text" value={formData.address || ''} onChange={(e) => { setFormData({ ...formData, address: e.target.value }); clearError('address'); }} data-field-error={fieldError('address')} className={inputClass('address')} placeholder="" />
                  </div>
                  <div className="flex-[1]">
                    <label className={feldLabel}>Nr.</label>
                    <input translate="no" type="text" value={formData.hausnummer || ''} onChange={(e) => setFormData({ ...formData, hausnummer: e.target.value })} className={inputClass('hausnummer')} placeholder="" />
                  </div>
                  <div className="flex-[5]" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <label className={feldLabel}>PLZ</label>
                    <input translate="no" type="text" inputMode="numeric" value={formData.plz || ''} onChange={(e) => handlePlzChange(e.target.value)} data-field-error={fieldError('plz')} className={inputClass('plz')} placeholder="" />
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <label className={feldLabel}>Ort</label>
                    <input translate="no" type="text" list="cm-plz-orte" value={formData.ort || ''} onChange={(e) => { setFormData({ ...formData, ort: e.target.value }); clearError('ort'); }} data-field-error={fieldError('ort')} className={inputClass('ort')} placeholder="" />
                    {ortSuggestions.length > 0 && (
                      <datalist id="cm-plz-orte">
                        {ortSuggestions.map((o) => <option key={o} value={o} />)}
                      </datalist>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className={feldLabel}>Land</label>
                    <input translate="no" type="text" value={formData.land || ''} onChange={(e) => setFormData({ ...formData, land: e.target.value })} className={inputClass('land')} placeholder="" />
                  </div>
                </div>
                </div>
              </div>

              {/* Kontakt & Bankverbindung */}
              <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}>
                <h4 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Kontakt &amp; Bankverbindung
                </h4>
                <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={feldLabel}>E-Mail</label>
                    <input translate="no" type="email" value={formData.email || ''} onChange={(e) => { setFormData({ ...formData, email: e.target.value }); clearError('email'); }} data-field-error={fieldError('email')} className={inputClass('email')} placeholder="" />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-24 flex-shrink-0">
                      <label className={feldLabel}>Vorwahl</label>
                      <input translate="no" type="tel" value={formData.phoneVorwahl || ''} onChange={(e) => setFormData({ ...formData, phoneVorwahl: e.target.value })} className={inputClass('phoneVorwahl')} placeholder="" />
                    </div>
                    <div className="flex-1">
                      <label className={feldLabel}>Rufnummer</label>
                      <input translate="no" type="tel" value={formData.phoneNummer || ''} onChange={(e) => setFormData({ ...formData, phoneNummer: e.target.value })} className={inputClass('phoneNummer')} placeholder="" />
                    </div>
                  </div>
                  <div>
                    <label className={feldLabel}>Verteiler-E-Mail (für uns)</label>
                    <input translate="no" type="email" value={formData.verteilerEmail || ''} onChange={(e) => { setFormData({ ...formData, verteilerEmail: e.target.value }); clearError('verteilerEmail'); }} data-field-error={fieldError('verteilerEmail')} className={inputClass('verteilerEmail')} placeholder="" />
                  </div>
                  <div>
                    <label className={feldLabel}>IBAN</label>
                    <input translate="no" type="text" value={formData.iban || ''} onChange={(e) => setFormData({ ...formData, iban: e.target.value })} className={inputClass('iban')} placeholder="" />
                  </div>
                  <div>
                    <label className={feldLabel}>Website</label>
                    <input translate="no" type="text" value={formData.website || ''} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className={inputClass('website')} placeholder="" />
                  </div>
                </div>
                </div>
              </div>

              {/* Unternehmensdaten */}
              <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}>
                <h4 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Unternehmensdaten
                </h4>
                <div className="space-y-4">

                  {/* Rechtsform Checkboxen */}
                  <div>
                    <label className={reqLabel('personentyp')}>Rechtsform</label>
                    <div className="flex flex-wrap gap-3">
                      {['Natürliche Person', 'Juristische Person'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="personentyp" checked={formData.personentyp === opt} onChange={() => setFormData({ ...formData, personentyp: opt })} className="w-4 h-4 cursor-pointer" />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Vorsteuerabzug Checkboxen */}
                  <div>
                    <label className={reqLabel('vorsteuerabzug')}>Vorsteuerabzugsberechtigt</label>
                    <div className="flex flex-wrap gap-3">
                      {['Ja', 'Nein', 'Teilweise'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="vorsteuerabzug" checked={formData.vorsteuerabzug === opt} onChange={() => setFormData({ ...formData, vorsteuerabzug: opt })} className="w-4 h-4 cursor-pointer" />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Natürliche Person: Steuer-ID + USt-IdNr. + Geburtsdatum */}
                  {formData.personentyp === 'Natürliche Person' && (
                    <>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <label className={feldLabel}>Steuer-Identifikationsnummer</label>
                          <input translate="no" type="text" value={formData.steueridentnummer || ''} onChange={(e) => setFormData({ ...formData, steueridentnummer: e.target.value })} className={inputClass('steueridentnummer')} placeholder="" maxLength={11}
                            onBlur={() => {
                              const v = formData.steueridentnummer || '';
                              if (formData.rechtsform === 'Einzelunternehmen' && istVermutlichSteuernummerStattIdnr(v)
                                && steuerIdWarnedValueRef.current !== v) {
                                steuerIdWarnedValueRef.current = v;
                                setSteuerIdWarnOpen(true);
                              }
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <label className={feldLabel}>USt-IdNr. (VAT-ID)</label>
                          <input translate="no" type="text" value={formData.taxId || ''} onChange={(e) => setFormData({ ...formData, taxId: e.target.value })} className={inputClass('taxId')} placeholder="" />
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="flex-[1]">
                          <label className={feldLabel}>Geburtsdatum</label>
                          <DateTextInputCustomer
                            value={formData.geburtsdatum || ''}
                            onChange={(val) => setFormData({ ...formData, geburtsdatum: val })}
                            darkMode={darkMode}
                            className={inputClass('geburtsdatum')}
                          />
                        </div>
                        <div className="flex-[1]" />
                      </div>
                    </>
                  )}

                  {/* Juristische Person: Art + Steuernummer + ELSTER + USt-IdNr. */}
                  {formData.personentyp === 'Juristische Person' && (() => {
                    const elster = computeElster(formData.steuernummer || '', formData.plz || '', formData.bundesland || '');
                    const stNrDigits = (formData.steuernummer || '').replace(/\D/g, '');
                    const blFromPlz = !formData.bundesland;
                    const resolvedBL = formData.bundesland || getElsterBundesland(formData.plz || '');
                    return (
                      <div className="space-y-4">
                        <div>
                          <label className={reqLabel('artJuristischePerson')}>Art der juristischen Person</label>
                          <div className="flex flex-wrap gap-3">
                            {['Privatrechtlich', 'Öffentlich-rechtlich'].map(opt => (
                              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="artJuristischePerson" checked={formData.artJuristischePerson === opt} onChange={() => setFormData({ ...formData, artJuristischePerson: opt })} className="w-4 h-4 cursor-pointer" />
                                <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{opt}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <label className={feldLabel}>Steuernummer</label>
                            <input translate="no" type="text" value={formData.steuernummer || ''} onChange={(e) => setFormData({ ...formData, steuernummer: e.target.value })} className={inputClass('steuernummer')} placeholder="" />
                          </div>
                          <div className="flex-1">
                            <label className={feldLabel}>USt-IdNr. (VAT-ID)</label>
                            <input translate="no" type="text" value={formData.taxId || ''} onChange={(e) => setFormData({ ...formData, taxId: e.target.value })} className={inputClass('taxId')} placeholder="" />
                          </div>
                        </div>
                        <div>
                          <label className={feldLabel}>
                            Bundesland des Finanzamts
                            <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>nur ändern, wenn ELSTER nicht stimmt</span>
                          </label>
                          <select translate="no" value={formData.bundesland || ''} onChange={(e) => setFormData({ ...formData, bundesland: e.target.value })} className={inputClass('bundesland')}>
                            <option value="">Automatisch aus PLZ</option>
                            {BUNDESLAENDER.map(bl => <option key={bl} value={bl}>{bl}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={feldLabel}>
                            ELSTER-Steuernummer (13-stellig)
                            <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>automatisch berechnet</span>
                          </label>
                          <input translate="no" type="text" value={elster} readOnly className={`w-full px-3 sm:px-4 py-2 border rounded-lg text-sm font-mono ${darkMode ? 'bg-slate-900 border-slate-600 text-green-400' : 'bg-slate-50 border-slate-300 text-slate-700'} cursor-default select-all`} placeholder={stNrDigits.length >= 10 ? 'PLZ oder Bundesland angeben' : '— Steuernummer eingeben —'} />
                          {stNrDigits.length > 0 && (
                            elster ? (
                              <p className={`mt-1.5 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Bundesland: <span className="font-medium">{resolvedBL}</span>{blFromPlz && ' (aus PLZ abgeleitet — bitte prüfen)'}
                              </p>
                            ) : (
                              <p className="mt-1.5 text-xs text-orange-600 dark:text-orange-400">
                                {!resolvedBL
                                  ? 'Kein Bundesland erkannt — bitte PLZ eintragen oder Bundesland wählen.'
                                  : 'ELSTER nicht berechenbar — Länge der Steuernummer prüfen (Landesnummer: 10 oder 11 Ziffern).'}
                              </p>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Gründungsdatum + Wirtschaftszweig */}
                  <div className="flex gap-4 items-start">
                    <div className="flex-[3]">
                      <label className={feldLabel}>Gründungsdatum *</label>
                      <DateTextInputCustomer value={formData.gruendung || ''} onChange={(val) => { setFormData({ ...formData, gruendung: val }); clearError('gruendung'); }} darkMode={darkMode} className={inputClass('gruendung')} hasError={!!fieldError('gruendung')} />
                    </div>
                    <div className="flex-[7]">
                      <label className={reqLabel('wirtschaftszweig')}>Wirtschaftszweig (WZ) *</label>
                      <div className={isMissing('wirtschaftszweig') ? 'rounded-lg ring-2 ring-orange-400' : ''}>
                        <WzSearch value={formData.wirtschaftszweig || ''} onChange={(val: string) => { setFormData({ ...formData, wirtschaftszweig: val }); clearError('wirtschaftszweig'); }} darkMode={darkMode} error={fieldError('wirtschaftszweig')} />
                      </div>
                    </div>
                  </div>

                  {/* Angemeldeter Geschäftsgegenstand */}
                  <div>
                    <label className={feldLabel}>
                      Angemeldeter Geschäftsgegenstand
                    </label>
                    <input
                      translate="no"
                      type="text"
                      value={formData.geschaeftsgegenstand || ''}
                      onChange={(e) => setFormData({ ...formData, geschaeftsgegenstand: e.target.value })}
                      className={inputClass('geschaeftsgegenstand')}
                    />
                  </div>

                  {/* Beschäftigte + Jahresumsatz + Jahresbilanzsumme */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={feldLabel}>Anzahl Beschäftigte</label>
                      <input translate="no" type="text" value={formData.mitarbeiterzahl || ''} onChange={(e) => setFormData({ ...formData, mitarbeiterzahl: e.target.value })} className={inputClass('mitarbeiterzahl')} placeholder="" />
                    </div>
                    <div className={formData.jahresbilanzsumme ? 'opacity-40' : ''}>
                      <label className={feldLabel}>Jahresumsatz (€)</label>
                      <input translate="no" type="text" value={formData.jahresumsatz || ''} disabled={!!formData.jahresbilanzsumme} onChange={(e) => setFormData({ ...formData, jahresumsatz: e.target.value })} className={`${inputClass('jahresumsatz')}${formData.jahresbilanzsumme ? ' cursor-not-allowed' : ''}`} placeholder="" />
                    </div>
                    <div className={formData.jahresumsatz ? 'opacity-40' : ''}>
                      <label className={feldLabel}>Jahresbilanzsumme (€)</label>
                      <input translate="no" type="text" value={formData.jahresbilanzsumme || ''} disabled={!!formData.jahresumsatz} onChange={(e) => setFormData({ ...formData, jahresbilanzsumme: e.target.value })} className={`${inputClass('jahresbilanzsumme')}${formData.jahresumsatz ? ' cursor-not-allowed' : ''}`} placeholder="" />
                    </div>
                  </div>

                  {/* Unternehmenstyp Checkboxen */}
                  <div>
                    <label className={reqLabel('unternehmenstyp')}>Unternehmenstyp</label>
                    <div className="flex flex-wrap gap-3">
                      {['Eigenständiges Unternehmen', 'Partnerunternehmen', 'Verbundenes Unternehmen'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="unternehmenstyp" checked={formData.unternehmenstyp === opt} onChange={() => setFormData({ ...formData, unternehmenstyp: opt })} className="w-4 h-4 cursor-pointer" />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Keine Beteiligung öffentlicher Stellen — eigener, hervorgehobener Punkt,
                      damit er nicht übersehen wird (BAFA-Erklärung: Häkchen = keine Beteiligung). */}
                  <div className={`rounded-lg border p-3 ${darkMode ? 'border-slate-600 bg-slate-800/50' : 'border-slate-200 bg-white'}`}>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!formData.oeffentlicheBeteiligung}
                        onChange={() => setFormData({ ...formData, oeffentlicheBeteiligung: !formData.oeffentlicheBeteiligung })}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className={`text-sm font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                        Keine Beteiligung öffentlicher Stellen
                      </span>
                    </label>
                  </div>

                  <label className="flex items-center gap-2 mt-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!formData.unternehmenInSchwierigkeiten}
                      onChange={() => setFormData({ ...formData, unternehmenInSchwierigkeiten: !formData.unternehmenInSchwierigkeiten })}
                      className="w-4 h-4 cursor-pointer"
                    />
                    <span className={`${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      Unternehmen in Schwierigkeiten (EU-Definition)
                    </span>
                  </label>
                  <FoerderListe
                    eintraege={(formData.foerderungen as FoerderEintrag[]) ?? []}
                    grenze={300000}
                    darkMode={darkMode}
                    onChange={(list) => setFormData({ ...formData, foerderungen: list })}
                  />

                </div>
              </div>

              {/* Geschäftsgegenstand + Notizen */}
              <div className={`rounded-lg p-4 ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}>
                <h4 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Zusätzliche Informationen
                </h4>
                <div>
                  <label className={feldLabel}>
                    Notizen
                  </label>
                  <textarea
                    translate="no"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none ${
                      darkMode
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-slate-300 text-slate-900'
                    }`}
                    placeholder="Zusätzliche Informationen..."
                  />
                </div>
              </div>
            </div>

            <div className={`shrink-0 p-4 sm:p-6 border-t space-y-3 ${
              darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            }`}>
              {saveError && (
                <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                  {saveError}
                </div>
              )}
              {editingCustomer?.foerderfaehigkeitAmpel && (
                <div className="mb-2">
                  <BafaCheckBadge
                    ampel={editingCustomer.foerderfaehigkeitAmpel}
                    reasons={editingCustomer.foerderfaehigkeitReasons ?? []}
                  />
                </div>
              )}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                  darkMode
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}>
                Abbrechen
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!(formData.name || '').trim()) {
                      setSaveError('Bitte zuerst den Firmennamen eingeben.');
                      return;
                    }
                    setDataRequestModalEmail(formData.email || '');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors font-medium cursor-pointer border border-purple-200"
                >
                  <MailPlus className="w-4 h-4" />
                  Daten anfordern
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="inline-flex">
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </span>
                  <span>{isSaving ? 'Wird gespeichert...' : 'Speichern'}</span>
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {dataRequestModalEmail !== null && (
        <DataRequestModal
          customerId={editingCustomer?.id}
          prefillEmail={dataRequestModalEmail}
          // Nur nötig, wenn die E-Mail neu/geändert ist (Seiteneffekt: PATCH/POST).
          // Bei unverändertem Bestandskunden kann der Formular-Schritt entfallen.
          onResolveCustomerId={
            editingCustomer && dataRequestModalEmail.trim() === (editingCustomer.email || '').trim()
              ? undefined
              : resolveCustomerForInvite
          }
          onClose={() => setDataRequestModalEmail(null)}
        />
      )}

      <ConfirmDialog
        open={deleteConfirmId !== null}
        title="Kunde löschen"
        message="Möchten Sie diesen Kunden wirklich löschen?"
        confirmLabel="Löschen"
        onConfirm={performDelete}
        onCancel={() => setDeleteConfirmId(null)}
      />

      <BafaCheckPopup
        open={checkPopup.open}
        ampel={checkPopup.ampel}
        reasons={checkPopup.reasons}
        onClose={() => setCheckPopup({ open: false })}
      />
      <SteuerIdWarnModal open={steuerIdWarnOpen} onClose={() => setSteuerIdWarnOpen(false)} />
    </>
  );
}